import React, { useState, useEffect, useRef } from 'react';
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    ZoomableGroup,
} from 'react-simple-maps';
import ApiClient from '../../utils/ApiClient';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const OUR_SELLER_ID = 'A11OMBDAB3LWS0';

function InventoryBuyboxMapReport() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [asins, setAsins] = useState([]);
    const [asinSearch, setAsinSearch] = useState('');
    const [selectedAsin, setSelectedAsin] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [mapData, setMapData] = useState(null);
    const [showFcMarkers, setShowFcMarkers] = useState(true);
    const [showBuyboxMarkers, setShowBuyboxMarkers] = useState(true);
    const [tooltip, setTooltip] = useState(null);
    const [position, setPosition] = useState({ coordinates: [-96, 38], zoom: 1 });
    const [inventoryOnly, setInventoryOnly] = useState(false);
    const [buyboxSummary, setBuyboxSummary] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [crawlActive, setCrawlActive] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchCheckDates();
    }, []);

    useEffect(() => {
        fetchAsins(inventoryOnly);
        fetchBuyboxSummary(selectedDate);
        checkCrawlActive(selectedDate);
    }, [inventoryOnly, selectedDate]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const checkCrawlActive = async (date) => {
        if (!date) { setCrawlActive(false); return; }
        const today = new Date().toISOString().split('T')[0];
        if (date !== today) { setCrawlActive(false); return; }
        try {
            const response = await ApiClient.get('/api/inventory-buybox-map/crawl-active');
            setCrawlActive(response.data?.active || false);
        } catch (err) {
            setCrawlActive(false);
        }
    };

    const fetchCheckDates = async () => {
        try {
            const response = await ApiClient.get('/api/inventory-buybox-map/check-dates');
            const dates = response.data || [];
            setAvailableDates(dates);
            if (dates.length > 0 && !selectedDate) {
                setSelectedDate(dates[0]);
            }
        } catch (err) {
            console.error('Error fetching check dates:', err);
        }
    };

    const fetchAsins = async (invOnly) => {
        try {
            const response = await ApiClient.get('/api/inventory-buybox-map/asins', {
                params: { inventoryOnly: invOnly }
            });
            setAsins(response.data || []);
        } catch (err) {
            console.error('Error fetching ASINs:', err);
        }
    };

    const fetchBuyboxSummary = async (date) => {
        try {
            const params = {};
            if (date) params.checkDate = date;
            const response = await ApiClient.get('/api/inventory-buybox-map/buybox-summary', { params });
            setBuyboxSummary(response.data || []);
        } catch (err) {
            console.error('Error fetching buybox summary:', err);
        }
    };

    const fetchMapData = async (asin, date) => {
        try {
            setLoading(true);
            setError(null);
            const body = { asin };
            if (date) body.checkDate = date;
            const response = await ApiClient.post('/api/inventory-buybox-map', body);
            setMapData(response.data);
        } catch (err) {
            setError('Failed to load map data');
            console.error('Error fetching map data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAsinSelect = (asin, title) => {
        setSelectedAsin(asin);
        setAsinSearch(title && title !== asin ? `${title} (${asin})` : asin);
        setShowDropdown(false);
        fetchMapData(asin, selectedDate);
    };

    const filteredAsins = asins.filter(a => {
        const search = asinSearch.toLowerCase();
        return a.asin.toLowerCase().includes(search) ||
               (a.title && a.title.toLowerCase().includes(search));
    });

    const getFcMarkerSize = (quantity) => {
        if (quantity >= 500) return 20;
        if (quantity >= 100) return 16;
        if (quantity >= 10) return 12;
        return 9;
    };

    const isOurSeller = (m) => m.sellerId === OUR_SELLER_ID;
    const buyboxWins = mapData?.buyboxMarkers?.filter(m => isOurSeller(m)).length || 0;
    const buyboxLosses = mapData?.buyboxMarkers?.filter(m => !isOurSeller(m)).length || 0;
    const totalFcLocations = mapData?.fcMarkers?.length || 0;
    const totalUnits = mapData?.fcMarkers?.reduce((sum, m) => sum + m.quantity, 0) || 0;

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Inventory & Buybox Map</h2>

            {/* ASIN Search + Date Picker */}
            <div className="flex items-end gap-4">
                <div className="flex-shrink-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check Date</label>
                    <select
                        value={selectedDate}
                        onChange={(e) => {
                            setSelectedDate(e.target.value);
                            if (selectedAsin) {
                                fetchMapData(selectedAsin, e.target.value);
                            }
                        }}
                        className="block w-full pl-3 pr-8 py-2 text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        {availableDates.map((d) => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                </div>
                <div className="relative flex-1 max-w-md" ref={dropdownRef}>
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">ASIN</label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={inventoryOnly}
                                onChange={(e) => {
                                    setInventoryOnly(e.target.checked);
                                    setAsinSearch('');
                                    setSelectedAsin('');
                                    setMapData(null);
                                }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                            />
                            <span className="text-xs text-gray-500">In stock only</span>
                        </label>
                    </div>
                    <input
                        type="text"
                        value={asinSearch}
                        onChange={(e) => {
                            setAsinSearch(e.target.value);
                            setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        placeholder="Search by ASIN or product title..."
                        className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {showDropdown && filteredAsins.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md overflow-auto border border-gray-200">
                            {filteredAsins.map((a) => (
                                <button
                                    key={a.asin}
                                    onClick={() => handleAsinSelect(a.asin, a.title)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-0"
                                >
                                    <span className="font-medium text-gray-900">{a.asin}</span>
                                    {a.title && a.title !== a.asin && (
                                        <span className="ml-2 text-gray-500 truncate">{a.title}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {crawlActive && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md text-sm flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-400 border-t-amber-700"></div>
                    <span className="font-medium">Data Collection In Progress</span>
                    <span className="text-amber-600">— results may be incomplete for today</span>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                </div>
            )}

            {loading && (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-3 text-gray-600">Loading map data...</span>
                </div>
            )}

            {mapData && !loading && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-blue-600">FC Locations</div>
                            <div className="text-2xl font-bold text-blue-900">{totalFcLocations}</div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-blue-600">Total Units</div>
                            <div className="text-2xl font-bold text-blue-900">{totalUnits.toLocaleString()}</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-green-600">Buybox Wins</div>
                            <div className="text-2xl font-bold text-green-900">{buyboxWins}</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-red-600">Buybox Losses</div>
                            <div className="text-2xl font-bold text-red-900">{buyboxLosses}</div>
                        </div>
                    </div>

                    {/* Toggle Controls */}
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showFcMarkers}
                                onChange={(e) => setShowFcMarkers(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
                            <span className="text-sm text-gray-700">FC Inventory ({totalFcLocations})</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showBuyboxMarkers}
                                onChange={(e) => setShowBuyboxMarkers(e.target.checked)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="inline-flex gap-1">
                                <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
                                <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
                            </span>
                            <span className="text-sm text-gray-700">Buybox ({buyboxWins + buyboxLosses})</span>
                        </label>
                    </div>

                    {/* Map */}
                    <div className="relative bg-gray-100 rounded-lg border border-gray-200 overflow-hidden" style={{ height: '600px' }}>
                        <ComposableMap
                            projection="geoAlbersUsa"
                            projectionConfig={{ scale: 1000 }}
                            style={{ width: '100%', height: '100%' }}
                        >
                            <ZoomableGroup
                                center={position.coordinates}
                                zoom={position.zoom}
                                onMoveEnd={(pos) => setPosition(pos)}
                            >
                                <Geographies geography={GEO_URL}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => (
                                            <Geography
                                                key={geo.rpiState || geo.properties.name}
                                                geography={geo}
                                                fill="#E5E7EB"
                                                stroke="#9CA3AF"
                                                strokeWidth={0.5}
                                                style={{
                                                    default: { outline: 'none' },
                                                    hover: { fill: '#D1D5DB', outline: 'none' },
                                                    pressed: { outline: 'none' },
                                                }}
                                            />
                                        ))
                                    }
                                </Geographies>

                                {/* Buybox Markers (render first so FC markers appear on top) */}
                                {showBuyboxMarkers && mapData.buyboxMarkers?.map((marker, i) => {
                                    const isWin = isOurSeller(marker);
                                    return (
                                        <Marker
                                            key={`bb-${i}`}
                                            coordinates={[marker.longitude, marker.latitude]}
                                            onMouseEnter={() => setTooltip({
                                                type: 'buybox',
                                                cityName: marker.cityName,
                                                sellerName: marker.sellerName,
                                                price: marker.price,
                                                isFba: marker.isFba,
                                                hasBuybox: marker.hasBuybox,
                                                isWin,
                                                checkDate: marker.checkDate,
                                            })}
                                            onMouseLeave={() => setTooltip(null)}
                                        >
                                            <circle
                                                r={10}
                                                fill={isWin ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)'}
                                                stroke={isWin ? '#15803D' : '#B91C1C'}
                                                strokeWidth={1.5}
                                            />
                                        </Marker>
                                    );
                                })}

                                {/* FC Inventory Markers (render last so they appear on top) */}
                                {showFcMarkers && mapData.fcMarkers?.map((marker, i) => (
                                    <Marker
                                        key={`fc-${i}`}
                                        coordinates={[marker.longitude, marker.latitude]}
                                        onMouseEnter={() => setTooltip({
                                            type: 'fc',
                                            fcCode: marker.fcCode,
                                            city: marker.city,
                                            state: marker.state,
                                            quantity: marker.quantity,
                                        })}
                                        onMouseLeave={() => setTooltip(null)}
                                    >
                                        <circle
                                            r={getFcMarkerSize(marker.quantity)}
                                            fill="rgba(59, 130, 246, 0.7)"
                                            stroke="#1D4ED8"
                                            strokeWidth={1}
                                        />
                                    </Marker>
                                ))}
                            </ZoomableGroup>
                        </ComposableMap>

                        {/* Tooltip */}
                        {tooltip && (
                            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-xs z-10">
                                {tooltip.type === 'fc' ? (
                                    <div>
                                        <div className="font-semibold text-blue-700">{tooltip.fcCode}</div>
                                        <div className="text-sm text-gray-600">{tooltip.city}, {tooltip.state}</div>
                                        <div className="text-sm font-medium mt-1">{tooltip.quantity.toLocaleString()} units</div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className={`font-semibold ${tooltip.isWin ? 'text-green-700' : 'text-red-700'}`}>
                                            {tooltip.cityName}
                                        </div>
                                        <div className="text-sm text-gray-600">{tooltip.sellerName}</div>
                                        <div className="text-sm mt-1">
                                            <span className="font-medium">${tooltip.price}</span>
                                            {tooltip.isFba && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">FBA</span>}
                                        </div>
                                        {!tooltip.hasBuybox && (
                                            <div className="text-xs text-gray-500 mt-1">No Buy Box (See All Options)</div>
                                        )}
                                        {tooltip.checkDate && (
                                            <div className="text-xs text-gray-400 mt-1">{tooltip.checkDate}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-4 h-4 rounded-full bg-blue-500 opacity-70"></span>
                            FC Inventory (size = quantity)
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-4 h-4 rounded-full bg-green-500 opacity-70"></span>
                            Buybox Win (Occam's Razor)
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-4 h-4 rounded-full bg-red-500 opacity-70"></span>
                            Buybox Loss (Competitor)
                        </div>
                    </div>
                </>
            )}

            {/* Buybox Wins by ASIN Chart */}
            {buyboxSummary.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Buybox Wins by ASIN</h3>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-3 h-3 rounded-sm bg-green-500 opacity-70"></span>
                            Occam's Razor Wins
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-3 h-3 rounded-sm bg-red-500 opacity-70"></span>
                            Competitor Wins
                        </span>
                        <span className="ml-auto text-gray-400">Click bar to load map</span>
                    </div>
                    <div className="space-y-1.5">
                        {buyboxSummary.map((d) => {
                            const total = d.wins + d.losses;
                            const pct = total > 0 ? Math.round((d.wins / total) * 100) : 0;
                            const winWidth = (d.wins / 50) * 100;
                            const lossWidth = (d.losses / 50) * 100;
                            const isSelected = d.asin === selectedAsin;
                            return (
                                <div key={d.asin} className={`flex items-center gap-3 py-1 px-2 rounded ${isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                                    <div className="w-28 flex-shrink-0">
                                        <span className="text-xs font-mono text-gray-800 select-all cursor-text">{d.asin}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div
                                            className="flex h-5 rounded overflow-hidden bg-gray-100 cursor-pointer"
                                            onClick={() => handleAsinSelect(d.asin, d.title)}
                                            title={`${d.title !== d.asin ? d.title + '\n' : ''}Wins: ${d.wins} | Losses: ${d.losses} | Win rate: ${pct}%`}
                                        >
                                            {d.wins > 0 && (
                                                <div
                                                    className="bg-green-500 opacity-70 transition-all"
                                                    style={{ width: `${winWidth}%` }}
                                                />
                                            )}
                                            {d.losses > 0 && (
                                                <div
                                                    className="bg-red-500 opacity-70 transition-all"
                                                    style={{ width: `${lossWidth}%` }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-24 flex-shrink-0 text-right">
                                        <span className="text-xs text-gray-500">{d.wins}/{total} ({pct}%)</span>
                                    </div>
                                    <div className="w-16 flex-shrink-0 text-right">
                                        <span className="text-xs text-blue-600 font-medium">{d.fcCount || 0} FCs</span>
                                    </div>
                                    <div className="w-20 flex-shrink-0 text-right">
                                        <span className="text-xs text-blue-600">{(d.totalUnits || 0).toLocaleString()} units</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!mapData && !loading && !error && (
                <div className="text-center py-12 text-gray-500">
                    Search for an ASIN above to load the inventory and buybox map.
                </div>
            )}
        </div>
    );
}

export default InventoryBuyboxMapReport;
